import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  ElementRef,
  ContentChild,
  TemplateRef
} from '@angular/core';
import { DropdownItem, Position, Alignment } from '../../../shared/models/design-system.types';

/**
 * Dropdown Component
 *
 * A flexible dropdown menu component.
 *
 * Usage:
 * ```html
 * <!-- With items array -->
 * <hc-dropdown [items]="menuItems" (itemClick)="onItemClick($event)">
 *   <button trigger class="btn btn-primary">Actions</button>
 * </hc-dropdown>
 *
 * <!-- With custom template -->
 * <hc-dropdown>
 *   <button trigger class="btn btn-primary">Menu</button>
 *   <ng-template #menu>
 *     <div class="custom-menu">Custom content</div>
 *   </ng-template>
 * </hc-dropdown>
 * ```
 */
@Component({
    selector: 'hc-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    standalone: false
})
export class DropdownComponent {
  /** Menu items to display */
  @Input() items: DropdownItem[] = [];

  /** Position relative to trigger */
  @Input() position: Position = 'bottom';

  /** Alignment relative to trigger */
  @Input() alignment: Alignment = 'start';

  /** Close on item click */
  @Input() closeOnSelect = true;

  /** Close on click outside */
  @Input() closeOnClickOutside = true;

  /** Minimum width of the dropdown */
  @Input() minWidth = '180px';

  /** Emits when an item is clicked */
  @Output() itemClick = new EventEmitter<DropdownItem>();

  /** Emits when dropdown opens */
  @Output() opened = new EventEmitter<void>();

  /** Emits when dropdown closes */
  @Output() closed = new EventEmitter<void>();

  /** Custom menu template */
  @ContentChild('menu') menuTemplate?: TemplateRef<unknown>;

  /** Whether the dropdown is open */
  isOpen = false;

  constructor(private elementRef: ElementRef) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen || !this.closeOnClickOutside) return;

    const target = event.target as HTMLElement;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen) {
      this.close();
    }
  }

  /**
   * Toggle the dropdown
   */
  toggle(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the dropdown
   */
  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.opened.emit();
  }

  /**
   * Close the dropdown
   */
  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.closed.emit();
  }

  /**
   * Handle item click
   */
  onItemClick(item: DropdownItem, event: MouseEvent): void {
    event.stopPropagation();

    if (item.disabled) return;

    if (item.action) {
      item.action();
    }

    this.itemClick.emit(item);

    if (this.closeOnSelect) {
      this.close();
    }
  }

  /**
   * Get position classes
   */
  get positionClasses(): string {
    return `hc-dropdown__menu--${this.position} hc-dropdown__menu--${this.alignment}`;
  }

  /**
   * Track function for ngFor
   */
  trackById(index: number, item: DropdownItem): string {
    return item.id;
  }
}
